import React, { useReducer, useRef, useEffect, useCallback, useMemo, useState, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { playerReducer, createInitialState } from './playerReducer';
import { useMediaEvents } from './hooks/useMediaEvents';
import { useKeyboardNav, KEYBOARD_SHORTCUTS } from './hooks/useKeyboardNav';
import { useFullscreen } from './hooks/useFullscreen';
import { useFrameAccurateTime } from './hooks/useFrameAccurateTime';
import { useCaptions } from './hooks/useCaptions';
import { useResponsiveControls } from './hooks/useResponsiveControls';
import { useHeatmapTracker } from './hooks/useHeatmapTracker';
import { useCaptionSpeech } from './hooks/useCaptionSpeech';
import { isSpeechSynthesisSupported } from './utils/captionSpeechSettings';
import { ControlBar } from './components/ControlBar';
import { PlayButton } from './components/PlayButton';
import { SkipBackButton } from './components/SkipBackButton';
import { SkipForwardButton } from './components/SkipForwardButton';
import { VolumeControl } from './components/VolumeControl';
import { SeekBar } from './components/SeekBar';
import { TimecodeDisplay } from './components/TimecodeDisplay';
import { TimecodePopover } from './components/TimecodePopover';
import { FullscreenButton } from './components/FullscreenButton';
// HeatmapButton removed — heatmap now appears automatically on seek bar hover
import { HeatmapOverlay } from './components/HeatmapOverlay';
import { ActionsMenu } from './components/ActionsMenu';
import { CaptionsButton } from './components/CaptionsButton';
import { CaptionOverlay } from './components/CaptionOverlay';
import { WaveformDisplay } from './components/WaveformDisplay';
import { DownloadOverlay } from './components/DownloadOverlay';
import { EncodingProgressOverlay } from './components/EncodingProgressOverlay';
import { UnplayableSourceOverlay } from './components/UnplayableSourceOverlay';
import { StatusOverlay } from './components/StatusOverlay';
import { SidePanel } from './components/SidePanel';
import { showPlaybackSettingsDialog } from './utils/playbackSettingsDialog';
import { usePlaybackErrorHandler } from './hooks/usePlaybackErrorHandler';
import { useStatusOverlay } from './hooks/useStatusOverlay';
import { useVideoOverlays } from './hooks/useVideoOverlays';
import { VideoOverlayLayer } from './components/VideoOverlayLayer';
import type { OverflowMenuItem } from './components/ActionsMenu';
import type { KollabPlayerProps, KollabPlayerAPI, CommentMarker, MediaSource } from './types';
import { captureFrame } from '../annotations/utils/frameExport';
import type { FrameExportOptions } from '../annotations/utils/frameExport';
import { computeAnnotationPosition } from './utils/computeAnnotationPosition';
import { timecodeToFrameCount } from './utils/timecodeFormatter';

const KollabPlayer = React.forwardRef<KollabPlayerAPI, KollabPlayerProps>(
  function KollabPlayer(props, ref) {
  const {
    mediaElementId,
    mediaType,
    fileId,
    waveformUrl,
    waveformColor,
    fps,
    startFrame: startFrameProp = 0,
    startTimecode,
    dropFrame = false,
    enableHeatmap = true,
    trackHeatmap = true,
    linkId,
    enableCaptions = true,
    autoplay = false,
    startTime,
    thumbnailSpriteUrl,
    comments: initialComments,
    onTimeUpdate,
    onPlay,
    onPause,
    onEnded,
    onSeek,
    onBeforeSeek,
    onFullscreenChange,
    canDownload = true,
    downloadUrl,
    fileTitle,
    fileTypeName,
    fileSize,
    fileIconUrl,
    sidePanelElementId,
    sidePanelWidth: sidePanelWidthProp,
    sidePanelTooltip,
    onMounted,
  } = props;

  // Convert startTimecode string to a frame offset. Takes precedence over startFrame.
  const startFrame = useMemo(() => {
    if (startTimecode && fps) {
      return timecodeToFrameCount(startTimecode, fps, dropFrame);
    }
    return startFrameProp;
  }, [startTimecode, fps, dropFrame, startFrameProp]);

  const [state, dispatch] = useReducer(playerReducer, undefined, createInitialState);
  const [comments, setComments] = useState<CommentMarker[]>(initialComments ?? []);
  const [detectedIsVideo, setDetectedIsVideo] = useState(true);
  const [annotationEditorActive, setAnnotationEditorActive] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLMediaElement | null>(null);
  const seekBarElRef = useRef<HTMLDivElement | null>(null);
  const [heatmapRect, setHeatmapRect] = useState<{ left: number; width: number } | null>(null);
  const [seekBarHovered, setSeekBarHovered] = useState(false);
  const fullscreenTargetRef = useRef<HTMLElement | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sidePanelContentRef = useRef<HTMLDivElement>(null);
  // Track adopted element and its original position for restoration
  const sidePanelAdoptedElRef = useRef<Element | null>(null);
  const sidePanelElementOriginRef = useRef<{ parent: HTMLElement; nextSibling: Node | null } | null>(null);
  const isOverControlsRef = useRef(false);
  const userPlaybackRateRef = useRef(1.0);
  const syncAnnotationRootsRef = useRef<(() => void) | null>(null);

  // Resolve media element reference and detect type
  useEffect(() => {
    const el = document.getElementById(mediaElementId) as HTMLMediaElement | null;
    mediaRef.current = el;
    if (el) {
      el.removeAttribute('controls');
      setDetectedIsVideo(el instanceof HTMLVideoElement);
      // The wrapper (e.g. #player2) is the parent that contains both
      // the <video> and the React controls mount point
      fullscreenTargetRef.current = el.parentElement;
      el.parentElement?.classList.add('kollab-player-wrapper');
    }
    return () => {
      fullscreenTargetRef.current?.classList.remove('kollab-player-wrapper');
    };
  }, [mediaElementId]);

  // Video fit — contract wrapper width to match video content edges when
  // the wrapper is height-constrained (e.g. max-height set on #player2).
  // Uses the video's aspect ratio to compute the correct width so there
  // are no black side-bars; whitespace appears around the player instead.
  const VIDEO_MIN_WIDTH = 300;
  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !(el instanceof HTMLVideoElement)) return;
    const wrapper = fullscreenTargetRef.current;
    if (!wrapper) return;

    let aspectRatio = 0;

    let lastMaxWidth = '';

    // Sync annotation roots to match the video's object-fit:contain area.
    // Computes the exact pixel position and size of the video content
    // within the wrapper, then positions the annotation root to match.
    const syncAnnotationRoots = () => {
      if (!aspectRatio) return;
      // Only sync viewer roots — editor roots are managed by index.ts syncEditorRoot
      const roots = wrapper.querySelectorAll('[data-annotation-root="viewer"]');
      if (roots.length === 0) return;

      const wrapperRect = wrapper.getBoundingClientRect();
      const videoRect = el.getBoundingClientRect();

      const pos = computeAnnotationPosition({
        videoNativeWidth: el.videoWidth,
        videoNativeHeight: el.videoHeight,
        videoElementWidth: el.clientWidth,
        videoElementHeight: el.clientHeight,
        videoTopInWrapper: videoRect.top - wrapperRect.top,
        videoLeftInWrapper: videoRect.left - wrapperRect.left,
      });
      if (!pos) return;

      roots.forEach((root) => {
        const htmlRoot = root as HTMLElement;
        htmlRoot.style.position = 'absolute';
        htmlRoot.style.width = pos.width + 'px';
        htmlRoot.style.height = pos.height + 'px';
        htmlRoot.style.top = pos.top + 'px';
        htmlRoot.style.left = pos.left + 'px';
        htmlRoot.style.right = 'auto';
        htmlRoot.style.bottom = 'auto';
        htmlRoot.style.margin = '0';
      });
    };

    syncAnnotationRootsRef.current = syncAnnotationRoots;

    const computeFit = () => {
      syncAnnotationRoots();

      if (!aspectRatio || state.isFullscreen) {
        if (lastMaxWidth !== '') {
          wrapper.style.maxWidth = '';
          wrapper.style.margin = '';
          lastMaxWidth = '';
        }
        return;
      }

      // When the annotation editor is active, the wrapper dimensions are
      // frozen so the player stays the same size while the video shrinks.
      if (wrapper.classList.contains('kollab-annotation-active')) return;

      const videoHeight = el.clientHeight;
      if (videoHeight <= 0) return;

      const contentWidth = Math.round(videoHeight * aspectRatio);
      const parentWidth = wrapper.parentElement?.clientWidth ?? contentWidth;

      let newMaxWidth: string;
      if (contentWidth < parentWidth - 1) {
        newMaxWidth = Math.max(contentWidth, VIDEO_MIN_WIDTH) + 'px';
      } else {
        newMaxWidth = '';
      }

      // Skip DOM writes when the value hasn't changed to avoid
      // ResizeObserver feedback loops that thrash layout.
      if (newMaxWidth !== lastMaxWidth) {
        lastMaxWidth = newMaxWidth;
        wrapper.style.maxWidth = newMaxWidth;
        wrapper.style.margin = newMaxWidth ? '0 auto' : '';
      }
    };

    const onMetadata = () => {
      if (el.videoHeight > 0) {
        aspectRatio = el.videoWidth / el.videoHeight;
        // Expose video aspect ratio for annotation overlay alignment
        wrapper.style.setProperty('--video-ar', `${el.videoWidth} / ${el.videoHeight}`);
        wrapper.style.setProperty('--video-ar-num', String(aspectRatio));
      }
      computeFit();
    };

    el.addEventListener('loadedmetadata', onMetadata);
    if (el.videoWidth > 0) onMetadata();

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => computeFit());
      observer.observe(wrapper);
      observer.observe(el); // sync annotation roots when video element resizes
      // Also observe the parent: when the viewport grows (e.g. DevTools
      // closes), the wrapper's maxWidth prevents IT from growing, so its
      // ResizeObserver won't fire.  The parent DOES grow, triggering a
      // recalculation that removes the stale maxWidth.
      if (wrapper.parentElement) observer.observe(wrapper.parentElement);
    }

    // Watch for annotation roots being added/removed so syncAnnotationRoots
    // runs immediately when a viewer annotation is shown (not just on resize).
    // Also tracks whether an annotation editor is present so the overlay layer
    // can hide mattes/safe areas while drawing.
    let mutationObs: MutationObserver | undefined;
    if (typeof MutationObserver !== 'undefined') {
      mutationObs = new MutationObserver((mutations) => {
        let needsSync = false;
        let editorChanged = false;
        for (const m of mutations) {
          for (const node of Array.from(m.addedNodes)) {
            if (node instanceof HTMLElement && node.hasAttribute('data-annotation-root')) {
              needsSync = true;
              if (node.getAttribute('data-annotation-root') === 'editor') editorChanged = true;
            }
          }
          for (const node of Array.from(m.removedNodes)) {
            if (node instanceof HTMLElement && node.hasAttribute('data-annotation-root')) {
              if (node.getAttribute('data-annotation-root') === 'editor') editorChanged = true;
            }
          }
        }
        if (needsSync) syncAnnotationRoots();
        if (editorChanged) {
          setAnnotationEditorActive(!!wrapper.querySelector('[data-annotation-root="editor"]'));
        }
      });
      mutationObs.observe(wrapper, { childList: true });
    }

    return () => {
      el.removeEventListener('loadedmetadata', onMetadata);
      observer?.disconnect();
      mutationObs?.disconnect();
      wrapper.style.maxWidth = '';
      wrapper.style.margin = '';
    };
  }, [mediaElementId, state.isFullscreen]);

  // Playback error handling — codec detection, error events, URL retry
  const { errorState, setError: showError, clearError, skipNextRetry } = usePlaybackErrorHandler(
    mediaRef.current,
    fileId,
    linkId,
  );

  const { status: overlayStatus, show: showStatus } = useStatusOverlay();

  const { activeMatte, setActiveMatte, activeSafeAreas, toggleSafeArea } = useVideoOverlays();

  const isVideo = mediaType ? mediaType === 'video' : detectedIsVideo;

  // Audio always shows the waveform area (image optional); video requires waveformUrl + toggle
  const showWaveform = isVideo
    ? (waveformUrl ? state.waveformVisible : false)
    : true;

  // Sync volume to media element and localStorage
  useEffect(() => {
    if (!mediaRef.current) return;
    mediaRef.current.volume = state.isMuted ? 0 : state.volume;
    try {
      localStorage.setItem('acornvolume', String(state.volume));
    } catch {
      // localStorage not available
    }
  }, [state.volume, state.isMuted]);

  // Sync loop to media element and localStorage
  useEffect(() => {
    if (mediaRef.current) {
      mediaRef.current.loop = state.isLooping;
    }
    try {
      localStorage.setItem('player.loop', String(state.isLooping));
    } catch {
      // localStorage not available
    }
  }, [state.isLooping]);

  // Sync playback settings to localStorage
  useEffect(() => {
    try { localStorage.setItem('player.settings.autoplay', String(state.autoplay)); } catch {}
  }, [state.autoplay]);

  useEffect(() => {
    try { localStorage.setItem('player.settings.showMarkers', String(state.showMarkers)); } catch {}
  }, [state.showMarkers]);

  useEffect(() => {
    try { localStorage.setItem('player.settings.pauseWhileTyping', String(state.pauseWhileTyping)); } catch {}
  }, [state.pauseWhileTyping]);

  useEffect(() => {
    try { localStorage.setItem('player.settings.autoCaptions', String(state.autoCaptions)); } catch {}
  }, [state.autoCaptions]);

  // Auto-enable captions when tracks become available
  useEffect(() => {
    if (state.autoCaptions && state.captionTracks.length > 0 && state.selectedTrackIdx === -1) {
      dispatch({ type: 'SELECT_TRACK', index: 0 });
    }
  }, [state.autoCaptions, state.captionTracks]);

  // Sync heatmap position to match the seek bar dimensions.
  // Uses a ResizeObserver so it updates when the player resizes.
  const seekBarRefCallback = useCallback((el: HTMLDivElement | null) => {
    seekBarElRef.current = el;
  }, []);

  useEffect(() => {
    const el = seekBarElRef.current;
    const container = containerRef.current;
    if (!el || !container || typeof ResizeObserver === 'undefined') return;

    const sync = () => {
      const barRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      setHeatmapRect({
        left: barRect.left - containerRect.left,
        width: barRect.width,
      });
    };

    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    ro.observe(container);
    return () => ro.disconnect();
  }, [seekBarElRef.current, containerRef.current]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collect sources from media element
  const sources: MediaSource[] = useMemo(() => {
    if (!mediaRef.current) return [];
    const sourceEls = mediaRef.current.querySelectorAll('source');
    return Array.from(sourceEls).map((el, i) => ({
      src: el.src,
      type: el.type || (isVideo ? 'video/mp4' : 'audio/mpeg'),
      label: el.dataset.label || (i === 0 ? 'Proxy' : 'Original'),
      proxyId: el.dataset.proxyId || undefined,
    }));
  }, [state.isReady, isVideo]); // Re-evaluate when media is ready

  // Media event bindings
  const callbacks = useMemo(
    () => ({ onTimeUpdate, onPlay, onPause, onEnded }),
    [onTimeUpdate, onPlay, onPause, onEnded],
  );
  useMediaEvents(mediaRef.current, dispatch, callbacks);

  // Heatmap playback tracking — sends bucket data to the server
  useHeatmapTracker(trackHeatmap ? mediaRef.current : null, fileId, linkId);

  // Frame-accurate time updates during playback (RAF-based)
  useFrameAccurateTime(mediaRef.current, state.isPlaying, fps, dispatch);

  // Fullscreen — fullscreen the wrapper element (parent of video + controls)
  const { toggleFullscreen, enterFullscreen, exitFullscreen } = useFullscreen(
    isVideo ? fullscreenTargetRef.current : null,
    dispatch,
    onFullscreenChange,
  );

  // Guard: check onBeforeSeek before allowing any seek operation.
  // After a cancellation, suppress further checks briefly to avoid
  // duplicate dialogs from React reconciling the range input value.
  const seekCancelledRef = useRef(false);
  const checkBeforeSeek = useCallback(() => {
    if (seekCancelledRef.current) return false;
    if (onBeforeSeek && !onBeforeSeek()) {
      seekCancelledRef.current = true;
      setTimeout(() => { seekCancelledRef.current = false; }, 100);
      return false;
    }
    return true;
  }, [onBeforeSeek]);

  // Marker navigation — standalone callbacks reused by both the keyboard
  // nav hook and the imperative API exposed via ref.
  const seekToNextMarker = useCallback(() => {
    if (!checkBeforeSeek()) return;
    const time = mediaRef.current?.currentTime ?? 0;
    const sorted = comments
      .filter((c) => c.reply <= 0 && !isNaN(c.pos))
      .sort((a, b) => a.pos - b.pos);
    const next = sorted.find((c) => c.pos > time + 0.1);
    if (next && mediaRef.current) {
      mediaRef.current.currentTime = next.pos;
      onSeek?.(next.pos);
    }
  }, [comments, onSeek, checkBeforeSeek]);

  const seekToPreviousMarker = useCallback(() => {
    if (!checkBeforeSeek()) return;
    const time = mediaRef.current?.currentTime ?? 0;
    const sorted = comments
      .filter((c) => c.reply <= 0 && !isNaN(c.pos))
      .sort((a, b) => b.pos - a.pos);
    const prev = sorted.find((c) => c.pos < time - 0.1);
    if (prev && mediaRef.current) {
      mediaRef.current.currentTime = prev.pos;
      onSeek?.(prev.pos);
    }
  }, [comments, onSeek, checkBeforeSeek]);

  const handleMuteToggle = useCallback(() => {
    dispatch({ type: 'TOGGLE_MUTE' });
    showStatus(state.isMuted ? 'Unmuted' : 'Muted');
  }, [state.isMuted, showStatus]);

  // Keyboard navigation
  useKeyboardNav({
    mediaEl: mediaRef.current,
    containerEl: containerRef.current,
    dispatch,
    fps: fps ?? 0,
    onSeek,
    onBeforeSeek,
    onFullscreenToggle: isVideo ? toggleFullscreen : undefined,
    onMuteToggle: handleMuteToggle,
    onNextMarker: seekToNextMarker,
    onPrevMarker: seekToPreviousMarker,
    onShowStatus: showStatus,
    basePlaybackRateRef: userPlaybackRateRef,
  });

  // Captions
  const { loadTrack } = useCaptions(
    mediaRef.current,
    dispatch,
    state.selectedTrackIdx,
    state.captions,
    state.currentTime,
  );

  // Caption speech synthesis
  const speechSupported = useMemo(() => isSpeechSynthesisSupported(), []);
  const { speechEnabled, setSpeechEnabled, showSettingsDialog: showSpeechSettings } = useCaptionSpeech(
    state.activeCueText,
    state.activeCueEnd,
    state.isPlaying,
    state.isSeeking,
    state.currentTime,
    mediaRef.current,
  );

  const handleToggleSpeech = useCallback(() => {
    setSpeechEnabled(!speechEnabled);
  }, [speechEnabled, setSpeechEnabled]);

  const handleOpenSpeechSettings = useCallback(() => {
    dispatch({ type: 'TOGGLE_CAPTION_PICKER' });
    showSpeechSettings();
  }, [showSpeechSettings]);

  // Load caption track when selection changes
  useEffect(() => {
    if (state.selectedTrackIdx >= 0 && state.captionTracks[state.selectedTrackIdx]) {
      loadTrack(state.captionTracks[state.selectedTrackIdx]);
    }
  }, [state.selectedTrackIdx, state.captionTracks, loadTrack]);

  // Seek to startTime once the media is ready.
  useEffect(() => {
    if (startTime && startTime > 0 && mediaRef.current && state.isReady) {
      mediaRef.current.currentTime = startTime;
      dispatch({ type: 'SEEK_END', time: startTime });
    }
  }, [state.isReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autoplay — respects both the prop and the user's settings toggle.
  useEffect(() => {
    if (autoplay && state.autoplay && mediaRef.current && state.isReady) {
      mediaRef.current.play().catch((err) => {
        console.warn('Autoplay blocked by browser:', err.message);
      });
    }
  }, [autoplay, state.autoplay, state.isReady]);

  // Remind user of non-1x playback speed when playback starts
  useEffect(() => {
    if (state.isPlaying && userPlaybackRateRef.current !== 1.0) {
      showStatus(`${userPlaybackRateRef.current}x`);
    }
  }, [state.isPlaying, showStatus]);

  // Auto-hide controls in fullscreen
  const resetHideTimer = useCallback(() => {
    if (!state.isFullscreen) return; // No-op outside fullscreen — avoids re-renders on every mousemove
    dispatch({ type: 'SET_CONTROLS_VISIBLE', visible: true });
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (!isOverControlsRef.current) {
      hideTimerRef.current = setTimeout(() => {
        dispatch({ type: 'SET_CONTROLS_VISIBLE', visible: false });
      }, 3000);
    }
  }, [state.isFullscreen]);

  // Keep controls visible while mouse is over them
  const handleControlsMouseEnter = useCallback(() => {
    isOverControlsRef.current = true;
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  const handleControlsMouseLeave = useCallback(() => {
    isOverControlsRef.current = false;
    if (state.isFullscreen) {
      hideTimerRef.current = setTimeout(() => {
        dispatch({ type: 'SET_CONTROLS_VISIBLE', visible: false });
      }, 3000);
    }
  }, [state.isFullscreen]);

  // In fullscreen, attach mousemove to the wrapper element so movement over
  // the video area (outside the React tree) still shows/hides controls
  useEffect(() => {
    const wrapper = fullscreenTargetRef.current;
    if (!state.isFullscreen || !wrapper) return;

    const handleWrapperMouseMove = () => resetHideTimer();
    wrapper.addEventListener('mousemove', handleWrapperMouseMove);
    return () => {
      wrapper.removeEventListener('mousemove', handleWrapperMouseMove);
    };
  }, [state.isFullscreen, resetHideTimer]);

  // Ensure controls are visible when leaving fullscreen — cancel any
  // pending hide timeout that was set while in fullscreen mode.
  // Also restore any adopted side panel element to its original position.
  useEffect(() => {
    if (!state.isFullscreen) {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      dispatch({ type: 'SET_CONTROLS_VISIBLE', visible: true });
      dispatch({ type: 'SET_SIDE_PANEL', open: false });

      // Restore adopted element to its original DOM position.
      // Uses the direct element ref (not panel.firstChild) because the
      // SidePanel portal may already be unmounted when this effect fires.
      const origin = sidePanelElementOriginRef.current;
      const el = sidePanelAdoptedElRef.current;
      if (origin && el) {
        if (origin.nextSibling) {
          origin.parent.insertBefore(el, origin.nextSibling);
        } else {
          origin.parent.appendChild(el);
        }
        sidePanelElementOriginRef.current = null;
        sidePanelAdoptedElRef.current = null;
      }
    }
  }, [state.isFullscreen]);

  // Sync sidePanelWidth prop to state
  useEffect(() => {
    if (sidePanelWidthProp != null && sidePanelWidthProp > 0) {
      dispatch({ type: 'SET_SIDE_PANEL_WIDTH', width: sidePanelWidthProp });
    }
  }, [sidePanelWidthProp]);

  // Toggle wrapper class to reserve space for the side panel when open.
  // The class adds padding-right so the video shrinks (object-fit: contain
  // letterboxes naturally) instead of the panel overlapping the video.
  // Also sets --side-panel-w CSS custom property for the panel width.
  useEffect(() => {
    const wrapper = fullscreenTargetRef.current;
    if (!wrapper) return;
    wrapper.style.setProperty('--side-panel-w', `${state.sidePanelWidth}px`);
    if (state.isFullscreen && state.sidePanelOpen) {
      wrapper.classList.add('kollab-player-sidepanel-active');
    } else {
      wrapper.classList.remove('kollab-player-sidepanel-active');
    }

    // Hide annotation roots during the sidebar transition, then show
    // them in the correct position after the animation completes.
    const roots = wrapper.querySelectorAll('[data-annotation-root]');
    roots.forEach((r) => (r as HTMLElement).style.visibility = 'hidden');
    const timerId = setTimeout(() => {
      syncAnnotationRootsRef.current?.();
      roots.forEach((r) => (r as HTMLElement).style.visibility = '');
    }, 320); // slightly longer than the 300ms CSS transition
    return () => clearTimeout(timerId);
  }, [state.isFullscreen, state.sidePanelOpen, state.sidePanelWidth]);

  // Toggle wrapper class when waveform is visible
  useEffect(() => {
    const wrapper = fullscreenTargetRef.current;
    if (!wrapper) return;
    if (showWaveform) {
      wrapper.classList.add('kollab-player-waveform-active');
    } else {
      wrapper.classList.remove('kollab-player-waveform-active');
    }
  }, [showWaveform]);

  // Helper: adopt a DOM element into the side panel (or restore the current one).
  // Sets hasElement immediately so the SidePanel renders (making the ref available).
  // If the panel ref isn't ready yet, the physical move is deferred to a retry effect.
  const adoptSidePanelElement = useCallback((elementId: string | null) => {
    const panel = sidePanelContentRef.current;

    // Restore any previously adopted element first
    const origin = sidePanelElementOriginRef.current;
    const adopted = sidePanelAdoptedElRef.current;
    if (origin && adopted) {
      if (origin.nextSibling) {
        origin.parent.insertBefore(adopted, origin.nextSibling);
      } else {
        origin.parent.appendChild(adopted);
      }
      sidePanelElementOriginRef.current = null;
      sidePanelAdoptedElRef.current = null;
    }

    if (!elementId) {
      dispatch({ type: 'SET_SIDE_PANEL_HAS_ELEMENT', hasElement: false });
      return;
    }

    // Signal intent so the SidePanel renders (toggle button becomes visible)
    dispatch({ type: 'SET_SIDE_PANEL_HAS_ELEMENT', hasElement: true });

    if (!panel) return; // Panel not rendered yet; will retry when fullscreen activates

    const el = document.getElementById(elementId);
    if (!el || !el.parentElement) return;

    // Already adopted (e.g. from a previous effect run)
    if (el.parentElement === panel) return;

    // Record original position for restoration on fullscreen exit
    sidePanelElementOriginRef.current = {
      parent: el.parentElement,
      nextSibling: el.nextSibling,
    };
    sidePanelAdoptedElRef.current = el;
    panel.appendChild(el);
  }, []);

  // Sync sidePanelElementId prop — adopt/restore when the prop changes or
  // when entering fullscreen (the panel div may not have existed on first attempt)
  useEffect(() => {
    if (state.isFullscreen) {
      adoptSidePanelElement(sidePanelElementId ?? null);
    }
  }, [sidePanelElementId, state.isFullscreen, adoptSidePanelElement]);

  // Expose API via ref for external code (supports multiple players)
  useImperativeHandle(ref, () => ({
    play: () => { mediaRef.current?.play(); },
    pause: () => { mediaRef.current?.pause(); },
    seekToTime: (time: number) => {
      if (mediaRef.current) {
        mediaRef.current.currentTime = time;
        dispatch({ type: 'SEEK_END', time });
        onSeek?.(time);
      }
    },
    isPlaying: () => !mediaRef.current?.paused,
    getPlaybackRate: () => mediaRef.current?.playbackRate ?? 1,
    getCurrentTime: () => mediaRef.current?.currentTime ?? 0,
    getDuration: () => mediaRef.current?.duration ?? 0,
    getMediaElement: () => mediaRef.current,
    getContainerElement: () => containerRef.current,
    updateComments: (newComments: CommentMarker[]) => setComments(newComments),
    showDownloadOverlay: (message?: string) => {
      showError(message || 'This file cannot be played.');
    },
    seekToNextMarker,
    seekToPreviousMarker,
    getKeyboardShortcuts: () => KEYBOARD_SHORTCUTS,
    captureFrame: (options?: FrameExportOptions) => {
      const video = mediaRef.current;
      if (!video || !(video instanceof HTMLVideoElement)) {
        return Promise.reject(new Error('No video element available'));
      }
      return captureFrame(video, options);
    },
    focus: (options?: FocusOptions) => { containerRef.current?.focus(options); },
    isFullscreen: () => state.isFullscreen,
    enterFullscreen,
    exitFullscreen,
    setSidePanelOpen: (open: boolean) => { dispatch({ type: 'SET_SIDE_PANEL', open }); },
    setSidePanelElement: (elementId: string | null) => {
      adoptSidePanelElement(elementId);
    },
    setSidePanelWidth: (width: number) => {
      dispatch({ type: 'SET_SIDE_PANEL_WIDTH', width });
    },
    selectCaptionLanguage: (lang: string | null) => {
      if (!lang) {
        dispatch({ type: 'SELECT_TRACK', index: -1 });
        return;
      }
      const idx = state.captionTracks.findIndex(
        (t) => t.srclang.toLowerCase() === lang.toLowerCase(),
      );
      if (idx >= 0) {
        dispatch({ type: 'SELECT_TRACK', index: idx });
      }
    },
    setWaveformVisible: (visible: boolean) =>
      dispatch({ type: 'SET_WAVEFORM_VISIBLE', visible }),
    getPauseWhileTyping: () => state.pauseWhileTyping,
    getAutoplay: () => state.autoplay,
    getShowMarkers: () => state.showMarkers,
    getAutoCaptions: () => state.autoCaptions,
  }), [onSeek, checkBeforeSeek, showError, comments, enterFullscreen, exitFullscreen, adoptSidePanelElement, state.isFullscreen, state.captionTracks, state.pauseWhileTyping, state.autoplay, state.showMarkers, state.autoCaptions]);

  // Fire onMounted callback once after the first render commit.
  useEffect(() => { onMounted?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync comments from props
  useEffect(() => {
    if (initialComments) {
      setComments(initialComments);
    }
  }, [initialComments]);

  // Event handlers
  const handlePlayClick = useCallback(() => {
    if (!mediaRef.current) return;
    if (mediaRef.current.paused) {
      if (!checkBeforeSeek()) return;
      mediaRef.current.playbackRate = userPlaybackRateRef.current;
      mediaRef.current.play();
    } else {
      mediaRef.current.pause();
    }
  }, [checkBeforeSeek]);

  // Click-to-play for video elements — also focus the container so
  // keyboard shortcuts work without having to click a control first.
  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !isVideo) return;

    const handleClick = () => {
      handlePlayClick();
      containerRef.current?.focus();
    };

    const preventContextMenu = (e: Event) => e.preventDefault();

    el.addEventListener('click', handleClick);
    el.addEventListener('contextmenu', preventContextMenu);
    return () => {
      el.removeEventListener('click', handleClick);
      el.removeEventListener('contextmenu', preventContextMenu);
    };
  }, [isVideo, handlePlayClick]);

  const handleSkipBack = useCallback(() => {
    if (!mediaRef.current) return;
    if (!checkBeforeSeek()) return;
    mediaRef.current.currentTime = Math.max(0, mediaRef.current.currentTime - 5);
    onSeek?.(mediaRef.current.currentTime);
  }, [onSeek, checkBeforeSeek]);

  const handleSkipForward = useCallback(() => {
    if (!mediaRef.current) return;
    if (!checkBeforeSeek()) return;
    mediaRef.current.currentTime = Math.min(mediaRef.current.duration, mediaRef.current.currentTime + 5);
    onSeek?.(mediaRef.current.currentTime);
  }, [onSeek, checkBeforeSeek]);

  const handleVolumeChange = useCallback(
    (volume: number) => {
      dispatch({ type: 'SET_VOLUME', volume });
      if (mediaRef.current) mediaRef.current.volume = volume;
    },
    [],
  );

  const handleSeekStart = useCallback(() => {
    dispatch({ type: 'SEEK_START' });

    // Dismiss annotation viewers when the user manually seeks via the
    // timeline. The viewer normally only auto-dismisses on 'play', so
    // seeking while paused leaves the annotation on screen.
    // Dispatch a custom event that mountAnnotationViewer listens for.
    // Programmatic seeks (e.g. seeking to a comment timecode) don't go
    // through the SeekBar, so they won't trigger this.
    mediaRef.current?.dispatchEvent(new Event('kollab:userseek'));
  }, []);

  const handleSeekEnd = useCallback(
    (time: number) => {
      if (!checkBeforeSeek()) return;
      if (mediaRef.current) {
        mediaRef.current.currentTime = time;
      }
      dispatch({ type: 'SEEK_END', time });
      onSeek?.(time);
    },
    [onSeek, checkBeforeSeek],
  );

  const handleSpeedChange = useCallback((rate: number) => {
    userPlaybackRateRef.current = rate;
    if (mediaRef.current) mediaRef.current.playbackRate = rate;
    dispatch({ type: 'SET_PLAYBACK_RATE', rate });
    showStatus(`${rate}x`);
  }, [showStatus]);

  const handleSourceChange = useCallback(
    (index: number) => {
      if (!mediaRef.current || !sources[index]) return;
      const wasPlaying = !mediaRef.current.paused;
      const savedTime = mediaRef.current.currentTime;

      clearError();
      // Prevent the error handler from silently retrying with a different
      // URL — the user explicitly chose this source. If it fails, the
      // error overlay will appear with an option to switch back.
      skipNextRetry();
      mediaRef.current.pause();
      mediaRef.current.src = sources[index].src;
      mediaRef.current.load();

      const onLoaded = () => {
        mediaRef.current!.removeEventListener('loadedmetadata', onLoaded);

        // Detect partially-playable files: the browser can decode audio
        // but not the video codec (e.g. ProRes in a MOV container).
        // videoWidth is 0 when no video frames can be decoded.
        // Only flag this for video/* MIME types — audio-only files in a
        // QuickTime container legitimately have videoWidth === 0.
        const sourceType = sources[index].type.split(';')[0].trim();
        if (
          mediaRef.current instanceof HTMLVideoElement &&
          mediaRef.current.videoWidth === 0 &&
          sourceType.startsWith('video/')
        ) {
          mediaRef.current.pause();
          showError('This file cannot be played in your browser.', true);
          return;
        }

        mediaRef.current!.currentTime = savedTime;
        if (wasPlaying) mediaRef.current!.play();
      };
      mediaRef.current.addEventListener('loadedmetadata', onLoaded);

      dispatch({ type: 'SWITCH_SOURCE', index });
    },
    [sources, clearError, skipNextRetry, showError],
  );

  const handleCaptionSelect = useCallback(
    (index: number) => {
      dispatch({ type: 'SELECT_TRACK', index });
    },
    [],
  );

  const handleMarkerSeek = useCallback(
    (time: number) => {
      if (!checkBeforeSeek()) return;
      dispatch({ type: 'SEEK_START' });
      if (mediaRef.current) {
        mediaRef.current.currentTime = time;
      }
      dispatch({ type: 'SEEK_END', time });
      onSeek?.(time);
    },
    [onSeek, checkBeforeSeek],
  );

  // Stable callbacks for menu toggles (avoids inline lambdas in JSX
  // which would defeat React.memo on child components)
  const handleSeekBarHover = useCallback((hovering: boolean) => {
    setSeekBarHovered(hovering);
  }, []);

  const handleToggleLoop = useCallback(() => {
    dispatch({ type: 'TOGGLE_LOOP' });
  }, []);

  const handleToggleCaptionPicker = useCallback(() => {
    dispatch({ type: 'TOGGLE_CAPTION_PICKER' });
  }, []);

  const handleToggleActionsMenu = useCallback(() => {
    dispatch({ type: 'TOGGLE_ACTIONS_MENU' });
  }, []);

  const handleToggleTimecodePopover = useCallback(() => {
    dispatch({ type: 'TOGGLE_TIMECODE_POPOVER' });
  }, []);

  const handleToggleAutoplay = useCallback(() => {
    dispatch({ type: 'TOGGLE_AUTOPLAY' });
  }, []);

  const handleToggleShowMarkers = useCallback(() => {
    dispatch({ type: 'TOGGLE_SHOW_MARKERS' });
  }, []);

  const handleTogglePauseWhileTyping = useCallback(() => {
    dispatch({ type: 'TOGGLE_PAUSE_WHILE_TYPING' });
  }, []);

  const handleOpenSettings = useCallback(() => {
    showPlaybackSettingsDialog(
      {
        autoplay: state.autoplay,
        showMarkers: state.showMarkers,
        pauseWhileTyping: state.pauseWhileTyping,
        autoCaptions: state.autoCaptions,
      },
      (key) => {
        if (key === 'autoplay') dispatch({ type: 'TOGGLE_AUTOPLAY' });
        else if (key === 'showMarkers') dispatch({ type: 'TOGGLE_SHOW_MARKERS' });
        else if (key === 'pauseWhileTyping') dispatch({ type: 'TOGGLE_PAUSE_WHILE_TYPING' });
        else if (key === 'autoCaptions') dispatch({ type: 'TOGGLE_AUTO_CAPTIONS' });
      },
    );
  }, [state.autoplay, state.showMarkers, state.pauseWhileTyping, state.autoCaptions]);

  // Simplified captions toggle for overflow menu (select first track or turn off)
  const handleCaptionsOverflowToggle = useCallback(() => {
    if (state.selectedTrackIdx >= 0) {
      dispatch({ type: 'SELECT_TRACK', index: -1 });
    } else if (state.captionTracks.length > 0) {
      dispatch({ type: 'SELECT_TRACK', index: 0 });
    }
  }, [state.selectedTrackIdx, state.captionTracks.length]);

  // --- Responsive button overflow ---
  // Buttons that can be collapsed into the Actions menu, ordered by priority
  // (highest priority = stays visible longest, collapses last)
  const hasCaptions = enableCaptions && state.captionTracks.length > 0;

  const collapsibleIds = useMemo(() => {
    const ids: string[] = [];
    ids.push('skip-back');                              // priority 1 (highest)
    ids.push('skip-forward');
    if (hasCaptions) ids.push('captions');
    return ids;
  }, [isVideo, hasCaptions]);

  // Hook computes mandatory width internally using breakpoint-aware metrics
  // that match the CSS container query rules in _react-player.scss
  const [controlsBottomRef, overflowed] = useResponsiveControls(collapsibleIds, isVideo);

  // Build overflow items for the ActionsMenu dropdown
  const overflowItems = useMemo<OverflowMenuItem[]>(() => {
    const items: OverflowMenuItem[] = [];

    if (overflowed.has('skip-back')) {
      items.push({
        id: 'skip-back',
        label: 'Back 5 seconds',
        type: 'action',
        onClick: handleSkipBack,
      });
    }

    if (overflowed.has('skip-forward')) {
      items.push({
        id: 'skip-forward',
        label: 'Forward 5 seconds',
        type: 'action',
        onClick: handleSkipForward,
      });
    }

    if (isVideo && waveformUrl) {
      items.push({
        id: 'waveform',
        label: 'Waveform',
        type: 'toggle' as const,
        isActive: state.waveformVisible,
        onClick: () => dispatch({ type: 'TOGGLE_WAVEFORM' }),
      });
    }

    if (overflowed.has('captions')) {
      items.push({
        id: 'captions',
        label: state.selectedTrackIdx >= 0
          ? `Captions: ${state.captionTracks[state.selectedTrackIdx]?.label ?? 'On'}`
          : 'Captions: Off',
        type: 'toggle',
        isActive: state.selectedTrackIdx >= 0,
        onClick: handleCaptionsOverflowToggle,
      });
    }

    return items;
  }, [
    overflowed,
    handleSkipBack,
    handleSkipForward,
    isVideo,
    waveformUrl,
    state.waveformVisible,
    state.selectedTrackIdx,
    state.captionTracks,
    handleCaptionsOverflowToggle,
  ]);

  return (
    <div
      ref={containerRef}
      className={`kollab-player ${isVideo ? 'kollab-player--video' : 'kollab-player--audio'}`}
      role="application"
      aria-label={isVideo ? 'Video Player' : 'Audio Player'}
      tabIndex={0}
      onMouseMove={resetHideTimer}
    >
      {/* Error overlays — portalled into wrapper so they cover the full
          player area (video + controls), not just the React mount point.
          Three variants:
            1. Unplayable source w/ proxy available → simple message + switch button
            2. Encoding in progress (no proxy yet) → polling progress overlay
            3. Generic error / download fallback → download overlay */}
      {fullscreenTargetRef.current && createPortal(
        errorState.hasError && state.activeSourceIndex > 0 && sources.length > 1 ? (
          <UnplayableSourceOverlay
            visible={errorState.hasError}
            message={errorState.message || 'This file cannot be played by your browser but a proxy is available.'}
            onSwitchToProxy={() => handleSourceChange(0)}
          />
        ) : errorState.isEncoding ? (
          <EncodingProgressOverlay
            visible={errorState.hasError}
            fileId={fileId}
            downloadUrl={downloadUrl}
            title={fileTitle}
            fileTypeName={fileTypeName}
            fileSize={fileSize}
            fileIconUrl={fileIconUrl}
          />
        ) : (
          <DownloadOverlay
            visible={errorState.hasError}
            message={errorState.message}
            canDownload={canDownload}
            downloadUrl={downloadUrl}
            title={fileTitle}
            fileTypeName={fileTypeName}
            fileSize={fileSize}
            fileIconUrl={fileIconUrl}
          />
        ),
        fullscreenTargetRef.current,
      )}

      {/* Loading overlay — absolute within parent (video only) */}
      {isVideo && state.isBuffering && !errorState.hasError && (
        <div className="kollab-player-loading" aria-label="Loading">
          <div className="spinner" />
        </div>
      )}

      {/* Caption overlay — absolute within parent for video, inline for audio */}
      {!errorState.hasError && !annotationEditorActive && <CaptionOverlay text={state.activeCueText} />}

      {/* Heatmap overlay — positioned at bottom of video (just above controls) */}
      {isVideo && !errorState.hasError && (
        <HeatmapOverlay
          visible={seekBarHovered && enableHeatmap}
          fileId={fileId}
          left={heatmapRect?.left}
          width={heatmapRect?.width}
        />
      )}

      {/* Video overlay layer — mattes and safe area guides */}
      {isVideo && !errorState.hasError && fullscreenTargetRef.current && mediaRef.current &&
        (mediaRef.current as HTMLVideoElement).videoWidth > 0 &&
        createPortal(
          <VideoOverlayLayer
            videoElement={mediaRef.current as HTMLVideoElement}
            activeMatte={activeMatte}
            activeSafeAreas={activeSafeAreas}
            hidden={annotationEditorActive}
          />,
          fullscreenTargetRef.current,
        )
      }

      {/* Status overlay — portalled into wrapper so it positions in the video area */}
      {isVideo && !errorState.hasError && fullscreenTargetRef.current && createPortal(
        <StatusOverlay
          message={overlayStatus.message}
          visible={overlayStatus.visible}
        />,
        fullscreenTargetRef.current,
      )}

      {/* Side panel — portalled into wrapper in fullscreen video mode.
          Always rendered so the content ref is available for element adoption;
          the toggle button is hidden when no element has been set. */}
      {isVideo && state.isFullscreen && !errorState.hasError && fullscreenTargetRef.current && createPortal(
        <SidePanel
          isOpen={state.sidePanelOpen}
          onToggle={() => dispatch({ type: 'TOGGLE_SIDE_PANEL' })}
          controlsVisible={state.controlsVisible}
          contentRef={sidePanelContentRef}
          hasElement={state.sidePanelHasElement}
          tooltip={sidePanelTooltip}
        />,
        fullscreenTargetRef.current,
      )}

      {/* Waveform display — portalled into wrapper */}
      {showWaveform && fullscreenTargetRef.current &&
        createPortal(
          <WaveformDisplay
            waveformUrl={waveformUrl}
            waveformColor={waveformColor}
            currentTime={state.currentTime}
            duration={state.duration}
            isPlaying={state.isPlaying}
            isSeeking={state.isSeeking}
            commentMarkers={state.showMarkers ? comments : undefined}
            fps={fps}
            startFrame={startFrame}
            dropFrame={dropFrame}
            onSeekStart={handleSeekStart}
            onSeekEnd={handleSeekEnd}
          />,
          fullscreenTargetRef.current,
        )
      }

      {/* Controls — in document flow below the media element */}
      {!errorState.hasError && <ControlBar
        visible={state.controlsVisible}
        isVideo={isVideo}
        onMouseEnter={handleControlsMouseEnter}
        onMouseLeave={handleControlsMouseLeave}
      >
        <div className="kollab-player-controls-top">
          {fps ? (
            <TimecodePopover
              value={state.currentTime}
              duration={state.duration}
              fps={fps}
              startFrame={startFrame}
              dropFrame={dropFrame}
              isOpen={state.timecodePopoverOpen}
              onToggle={handleToggleTimecodePopover}
              onSeek={handleSeekEnd}
            />
          ) : (
            <div className="kollab-player-timecode-container">
              <TimecodeDisplay value={state.currentTime} />
            </div>
          )}
          <SeekBar
            currentTime={state.currentTime}
            duration={state.duration}
            bufferedEnd={state.bufferedEnd}
            mediaElementId={mediaElementId}
            onSeekStart={handleSeekStart}
            onSeekEnd={handleSeekEnd}
            fps={fps}
            startFrame={startFrame}
            dropFrame={dropFrame}
            thumbnailSpriteUrl={thumbnailSpriteUrl}
            comments={state.showMarkers ? comments : undefined}
            onMarkerClick={handleMarkerSeek}
            barRefCallback={seekBarRefCallback}
            onHoverChange={handleSeekBarHover}
          />
          <div className="kollab-player-timecode-container">
            <TimecodeDisplay
              value={state.duration}
              fps={fps}
              startFrame={startFrame}
              dropFrame={dropFrame}
            />
          </div>
        </div>

        <div ref={controlsBottomRef} className="kollab-player-controls-bottom">
          <div className="kollab-player-controls-left">
            <PlayButton
              isPlaying={state.isPlaying}
              mediaElementId={mediaElementId}
              onClick={handlePlayClick}
            />
            {!overflowed.has('skip-back') && (
              <SkipBackButton
                mediaElementId={mediaElementId}
                onClick={handleSkipBack}
              />
            )}
            {!overflowed.has('skip-forward') && (
                <SkipForwardButton
                    mediaElementId={mediaElementId}
                    onClick={handleSkipForward}
                />
            )}
            <VolumeControl
              volume={state.volume}
              isMuted={state.isMuted}
              mediaElementId={mediaElementId}
              onVolumeChange={handleVolumeChange}
              onMuteToggle={handleMuteToggle}
            />
          </div>

          <div className="kollab-player-controls-right">
            {enableCaptions && !overflowed.has('captions') && (
              <CaptionsButton
                tracks={state.captionTracks}
                selectedTrackIdx={state.selectedTrackIdx}
                isOpen={state.captionPickerOpen}
                onToggle={handleToggleCaptionPicker}
                onSelectTrack={handleCaptionSelect}
                speechEnabled={speechEnabled}
                speechSupported={speechSupported}
                onToggleSpeech={handleToggleSpeech}
                onOpenSpeechSettings={handleOpenSpeechSettings}
              />
            )}
            <ActionsMenu
              isOpen={state.actionsMenuOpen}
              playbackRate={state.playbackRate}
              isLooping={state.isLooping}
              sources={sources}
              activeSourceIndex={state.activeSourceIndex}
              overflowItems={overflowItems}
              onToggle={handleToggleActionsMenu}
              onSpeedChange={handleSpeedChange}
              onSourceChange={handleSourceChange}
              onToggleLoop={handleToggleLoop}
              onOpenSettings={handleOpenSettings}
              overlayMatte={isVideo ? activeMatte : undefined}
              overlaySafeAreas={isVideo ? activeSafeAreas : undefined}
              onOverlayMatteChange={isVideo ? setActiveMatte : undefined}
              onToggleOverlaySafeArea={isVideo ? toggleSafeArea : undefined}
            />
            {isVideo && (
              <FullscreenButton
                isFullscreen={state.isFullscreen}
                onClick={toggleFullscreen}
              />
            )}
          </div>
        </div>
      </ControlBar>}

    </div>
  );
});

export default KollabPlayer;
